import { useCallback } from 'react'
import { atom, useAtomValue, useStore } from 'jotai'
import { GlobalState } from '@highlight/ipc'
import { useGlobalState } from '@highlight/ipc/react'
import { OVERLAY_DISPLAY_ID_STATE_KEY, overlayDisplayKey } from '@shared/overlay/display'

/**
 * Non-`undefined` while a drop is being committed, pinning the key to the
 * monitor the readers are already painted against.
 *
 * Main publishes `overlay.displayId` from inside the window move, so on a
 * cross-monitor drop it reaches the renderer *before* the ping reply that
 * tells the drop which monitor to store against. Those are two arrivals of one
 * change, and between them the destination's key resolves against the
 * destination's *previous* geometry: the panel's top and height jump, and
 * Mini springs toward wherever it was last parked over there, both of which
 * the commit then undoes. Holding the key collapses that into one update.
 */
const heldDisplayKeyAtom = atom<string | null | undefined>(undefined)

/**
 * Storage key for the monitor the overlay is currently on, or `null` before
 * main has published one. Per-display preferences (sidepanel geometry, Mini's
 * position) read and write under it.
 */
export function useOverlayDisplayKey(): string | null {
  const [displayId] = useGlobalState<number | undefined>(OVERLAY_DISPLAY_ID_STATE_KEY)
  const held = useAtomValue(heldDisplayKeyAtom)
  return held === undefined ? overlayDisplayKey(displayId) : held
}

/**
 * Freezes the display key at its current value. Call at the end of a drag and
 * release in the same commit that stores the drop, so readers move to the new
 * monitor and its new geometry together.
 *
 * Reads the display id straight off the replica rather than through the hook,
 * because a drag end has to hold the key that is live at pointerup, not the one
 * from the render that registered the handler.
 */
export function useHoldOverlayDisplayKey(): () => () => void {
  const store = useStore()

  return useCallback(() => {
    store.set(heldDisplayKeyAtom, overlayDisplayKey(GlobalState.get<number>(OVERLAY_DISPLAY_ID_STATE_KEY)))
    return () => store.set(heldDisplayKeyAtom, undefined)
  }, [store])
}
